import { useCallback, useEffect, useRef } from 'react';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useCurrentCaseClaimedStatus } from 'hooks/useCurrentCaseClaimedStatus';
import usePrevious from 'react-use/esm/usePrevious';
import { useMutation } from '@apollo/client';
import { CREATE_WORKLIST_ITEM_ACTIVITY } from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { WithApolloContext } from 'modules/Apollo/types';
import { ReportStatus, ReportStatuses } from '../state';

export const HEARTBEAT_FREQUENCY = 30000;

export const useReporterHeartbeat = ({
  customSendHeartbeat,
  heartbeatFrequency = HEARTBEAT_FREQUENCY,
  context,
  reportStatus,
}: {
  reportStatus: ReportStatuses;
  customSendHeartbeat?: () => void;
  heartbeatFrequency?: number;
} & WithApolloContext) => {
  const currentCaseId = useCurrentCaseId();
  const previousCaseId = usePrevious(currentCaseId);
  const { isClaimedByMe } = useCurrentCaseClaimedStatus({ context });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [createWorklistItemActivityMutation] = useMutation(CREATE_WORKLIST_ITEM_ACTIVITY, {
    context,
  });

  /**
   * Send worklist item activity to keep the case active
   */
  const sendHeartbeat = useCallback(async () => {
    if (customSendHeartbeat) {
      customSendHeartbeat();
    } else if (currentCaseId != null) {
      await createWorklistItemActivityMutation({
        variables: { smid: currentCaseId },
        onError: (error) => {
          logger.error(
            `[useReporterHeartbeat] - request to create worklistItem activity for caseId ${currentCaseId} failed.`,
            error
          );
        },
      });
    }
  }, [customSendHeartbeat, currentCaseId, createWorklistItemActivityMutation]);

  useEffect(() => {
    // Trust the Reporter mount point when extension window state is unavailable.
    if (
      currentCaseId != null &&
      isClaimedByMe === true &&
      reportStatus !== ReportStatus.Submitted
    ) {
      if (!intervalRef.current || previousCaseId !== currentCaseId) {
        sendHeartbeat(); // Send heartbeat immediately if no interval is active or case ID changes
      }

      intervalRef.current = setInterval(sendHeartbeat, heartbeatFrequency);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [
    currentCaseId,
    isClaimedByMe,
    sendHeartbeat,
    previousCaseId,
    heartbeatFrequency,
    reportStatus,
  ]);

  return { sendHeartbeat };
};
